import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Popconfirm, Table } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';
import { Box } from 'atoms';
import StatusChip from 'componentsv2/common/StatusChip';
import { numberWithCommas, rateWithCommas } from 'utils/NumberCommaSeprator';
import {
	formatIstDateTime,
	LINE_ITEM_BAG,
	LINE_ITEM_MANPOWER,
	LINE_ITEM_SHIPMENT,
} from '../warehousing.constants';
import {
	AuditTrail,
	EmptyState,
	LineItemsTitle,
	MiniPanel,
	MiniPanelRow,
	NestedTable,
	NumCell,
} from './warehousingStyles';
import DailyCountEditor from './DailyCountEditor';
import SlabBreakdownModal from './SlabBreakdownModal';

const money = (value) =>
	value === null || value === undefined ? '—' : `₹ ${numberWithCommas(Math.round(value))}`;

// Bag counts exist only when a per-bag (Cross Dock) rate is configured.
const isBagCountEnabled = (bagRate) =>
	bagRate !== undefined && bagRate !== null && Number(bagRate) > 0;

// Per-head figures keep their decimals (a ₹10.50 head must not read as ₹11), since the
// total is that figure x Count.
const perHead = (value) =>
	value === null || value === undefined ? '—' : `₹ ${rateWithCommas(value)}`;

// Totals for one manpower row. The API sends them; the fallback keeps an older response usable.
const rowTotalCost = (i) => Number(i.total_cost ?? Number(i.cost || 0) * Number(i.headcount || 0));
const rowTotalCtcu = (i) =>
	i.cost_to_customer === null || i.cost_to_customer === undefined
		? 0
		: Number(i.total_cost_to_customer ?? Number(i.cost_to_customer) * Number(i.headcount || 0));

// When the row was actually recorded, which is not the same as the date it is filed
// against — a back-dated CSV is exactly the case this line explains to the operator.
const uploadedOn = (entry) => (entry?.created_at ? String(entry.created_at).slice(0, 10) : null);

const sourceChip = (value) =>
	value === 'csv' ? (
		<StatusChip tone="default" label="CSV" />
	) : (
		<StatusChip tone="primary" label="Manual" />
	);

/**
 * Line items for one date, split by type.
 *
 * Duplicate rows for the same date and type are allowed by design, so nothing is
 * de-duplicated. Rows key off the server-side line item id so deletes target the right one.
 *
 * The date is carried by the group heading, not a column — it is the same on every row.
 * Every numeric column is given an explicit width so the header, the body and the
 * subtotal row are laid out on one set of widths instead of three independent guesses.
 */
export default function DailyDatePanel({
	date,
	cumulativeBreakdown,
	cumulativeShipmentCount,
	canDelete,
	canEditCounts,
	costFrozen,
	revenueFrozen,
	revenueFrozenReason,
	bagRate,
	bagRateLabel,
	isCostPlus,
	onDelete,
	onUpdateCount,
}) {
	// Above the early return below: hook order has to be the same on every render.
	const [breakdownOpen, setBreakdownOpen] = useState(false);
	// The slab walk behind this day's shipments, as computed by the revenue engine.
	// Empty for a cost_plus project (no slabs) and for a day with no shipments.
	const slabBreakdown = date?.slab_breakdown || [];

	const items = date?.line_items || [];
	// Bag counts are only collected when Cross Dock is configured on the project, so a
	// project without a per-bag rate shows no bag figure at all.
	const bagsEnabled = isBagCountEnabled(bagRate);
	const manpower = items.filter((i) => i.type === LINE_ITEM_MANPOWER);
	const shipments = items.filter((i) => i.type === LINE_ITEM_SHIPMENT);
	const bags = items.filter((i) => i.type === LINE_ITEM_BAG);
	// Cost Plus bills on cost, not shipments: no Shipment Count panel for it.
	const showShipments = !isCostPlus;

	if (items.length === 0) {
		return (
			<EmptyState>
				<div className="empty-body">No line items recorded for this date.</div>
			</EmptyState>
		);
	}

	const baseManpowerColumns = [
		{
			title: 'Manpower Type',
			dataIndex: 'manpower_type_name',
			key: 'manpower_type_name',
			render: (v) => v || '—',
		},
		{
			title: isCostPlus ? 'Cost to Company / Head' : 'Cost / Head',
			dataIndex: 'cost',
			key: 'cost',
			width: 150,
			align: 'right',
			render: (v) => <NumCell>{perHead(v)}</NumCell>,
		},
		...(isCostPlus
			? [
					{
						title: 'Cost to Customer / Head',
						dataIndex: 'cost_to_customer',
						key: 'cost_to_customer',
						width: 170,
						align: 'right',
						render: (v) => <NumCell>{perHead(v)}</NumCell>,
					},
			  ]
			: []),
		{
			title: 'Count',
			dataIndex: 'headcount',
			key: 'headcount',
			width: 90,
			align: 'right',
			render: (v) => <NumCell>{numberWithCommas(v || 0)}</NumCell>,
		},
		{
			title: isCostPlus ? 'Total Cost to Company' : 'Total Cost',
			key: 'total_cost',
			width: 150,
			align: 'right',
			render: (_, record) => <NumCell>{money(rowTotalCost(record))}</NumCell>,
		},
		...(isCostPlus
			? [
					{
						title: 'Total Cost to Customer',
						key: 'total_cost_to_customer',
						width: 170,
						align: 'right',
						render: (_, record) => (
							<NumCell>
								{record.cost_to_customer === null ||
								record.cost_to_customer === undefined
									? '—'
									: money(rowTotalCtcu(record))}
							</NumCell>
						),
					},
					{
						title: 'Generated Revenue',
						dataIndex: 'generated_revenue',
						key: 'generated_revenue',
						width: 150,
						align: 'right',
						render: (v) => <NumCell>{money(v)}</NumCell>,
					},
			  ]
			: []),
		{
			title: 'Hours',
			dataIndex: 'hours',
			key: 'hours',
			width: 100,
			align: 'right',
			// Only overtime-flagged manpower types carry hours
			render: (v) => (
				<NumCell>{v === null || v === undefined ? '—' : numberWithCommas(v)}</NumCell>
			),
		},
		{ title: 'Source', dataIndex: 'source', key: 'source', width: 110, render: sourceChip },
	];

	const deleteColumn = {
		title: '',
		key: 'delete',
		width: 56,
		align: 'center',
		render: (_, record) => (
			<Popconfirm
				title="Delete this line item?"
				description="It is removed from the day's totals. The audit trail keeps a record."
				okText="Delete"
				okButtonProps={{ danger: true }}
				onConfirm={() => onDelete(record.type, record.id)}
			>
				<DeleteOutlined
					style={{ color: '#ff4d4f', cursor: 'pointer' }}
					aria-label="Delete line item"
				/>
			</Popconfirm>
		),
	};

	const canDeleteManpower = canDelete && !costFrozen && !revenueFrozen;
	const manpowerColumns = canDeleteManpower
		? [...baseManpowerColumns, deleteColumn]
		: baseManpowerColumns;

	const manpowerTotal = manpower.reduce((s, i) => s + rowTotalCost(i), 0);
	const manpowerCtcuTotal = manpower.reduce((s, i) => s + rowTotalCtcu(i), 0);
	const manpowerGeneratedRevenueTotal = manpower.reduce(
		(s, i) => s + (Number(i.generated_revenue) || 0),
		0
	);
	const headcountTotal = manpower.reduce((s, i) => s + (Number(i.headcount) || 0), 0);
	const hoursTotal = manpower.reduce((s, i) => s + (Number(i.hours) || 0), 0);
	const shipmentTotal = shipments.reduce((s, i) => s + (Number(i.shipment_count) || 0), 0);

	const bagTotal = bags.reduce((s, i) => s + (Number(i.bag_count) || 0), 0);

	// One summary cell per column, in column order, each carrying its column's alignment —
	// that is what keeps the subtotal sitting under the figures it totals. Built from an
	// array rather than fixed indices, since the Cost Plus columns above change the count.
	const manpowerSummary = () => {
		const cells = [
			<strong key="label">Subtotal</strong>,
			null, // Cost / Head: a per-head rate has no subtotal
			...(isCostPlus ? [null] : []), // Cost to Customer / Head
			<NumCell key="headcount">
				<strong>{numberWithCommas(headcountTotal)}</strong>
			</NumCell>,
			<NumCell key="cost">
				<strong>{money(manpowerTotal)}</strong>
			</NumCell>,
			...(isCostPlus
				? [
						<NumCell key="ctcu">
							<strong>{money(manpowerCtcuTotal)}</strong>
						</NumCell>,
						<NumCell key="generated_revenue">
							<strong>{money(manpowerGeneratedRevenueTotal)}</strong>
						</NumCell>,
				  ]
				: []),
			<NumCell key="hours">
				<strong>{hoursTotal > 0 ? numberWithCommas(hoursTotal) : '—'}</strong>
			</NumCell>,
			null, // Source
			...(canDeleteManpower ? [null] : []),
		];
		return (
			<Table.Summary>
				<Table.Summary.Row>
					{cells.map((content, index) => (
						<Table.Summary.Cell
							// eslint-disable-next-line react/no-array-index-key -- cells are positional, no stable id
							key={index}
							index={index}
							align={index === 0 ? 'left' : 'right'}
						>
							{content}
						</Table.Summary.Cell>
					))}
				</Table.Summary.Row>
			</Table.Summary>
		);
	};

	return (
		<Box>
			{manpower.length > 0 && (
				<Box>
					<LineItemsTitle>Manpower — {date.entry_date}</LineItemsTitle>
					<NestedTable
						rowKey={(record, index) => record.id ?? `mp-${date.entry_date}-${index}`}
						size="small"
						columns={manpowerColumns}
						dataSource={manpower}
						pagination={false}
						// antd's summary API takes a render function by design
						// eslint-disable-next-line react/no-unstable-nested-components
						summary={manpowerSummary}
					/>
				</Box>
			)}

			{/*
			 * Shipments and bags each carry one figure per row rather than the five columns
			 * of chrome a table would wrap around a single value, so they render as mini
			 * metric panels. The date is already the parent row's own label.
			 *
			 * One figure per type — the date's total, the same number the collapsed row and
			 * the dashboard use. A date can hold several rows server-side (a CSV batch plus
			 * a later manual add); listing them individually read as a double count.
			 *
			 * Adjust writes to a single row by id, so it is only offered when one row backs
			 * the figure. Splitting an edited total back across several rows is not
			 * something the client can do correctly.
			 */}
			{(showShipments || bagsEnabled) && (
				<Box mt={manpower.length > 0 ? '16px' : '0'}>
					<MiniPanelRow>
						{showShipments && (
							<MiniPanel frozen={revenueFrozen}>
								<div className="mini-panel-label">Shipment Count</div>
								{shipments.length === 0 ? (
									<div className="mini-panel-value">—</div>
								) : (
									<DailyCountEditor
										entryType={LINE_ITEM_SHIPMENT}
										entryId={shipments[0].id}
										value={shipmentTotal}
										uploadedOn={uploadedOn(shipments[0])}
										frozen={revenueFrozen}
										frozenReason={revenueFrozenReason}
										canEdit={canEditCounts && shipments.length === 1}
										onSave={onUpdateCount}
									/>
								)}
								{shipments.length > 1 && (
									<div className="mini-panel-sub">
										Total of {shipments.length} entries — adjust from the sheet
										or delete an entry to change it.
									</div>
								)}
								{slabBreakdown.length > 0 && (
									<button
										type="button"
										className="mini-panel-link"
										onClick={() => setBreakdownOpen(true)}
									>
										Breakdown
									</button>
								)}
							</MiniPanel>
						)}

						{bagsEnabled && (
							<MiniPanel frozen={revenueFrozen}>
								<div className="mini-panel-label">Bag Count</div>
								{bags.length === 0 ? (
									<div className="mini-panel-value">—</div>
								) : (
									<DailyCountEditor
										entryType={LINE_ITEM_BAG}
										entryId={bags[0].id}
										value={bagTotal}
										uploadedOn={uploadedOn(bags[0])}
										rate={bagRate}
										rateLabel={bagRateLabel}
										frozen={revenueFrozen}
										frozenReason={revenueFrozenReason}
										canEdit={canEditCounts && bags.length === 1}
										onSave={onUpdateCount}
									/>
								)}
								{bags.length > 1 && (
									<div className="mini-panel-sub">
										Total of {bags.length} entries — adjust from the sheet or
										delete an entry to change it.
									</div>
								)}
							</MiniPanel>
						)}
					</MiniPanelRow>
				</Box>
			)}
			{/* The audit trail belongs to the per-line-item detail; a shipment-only day
			    shows a single figure, so there is nothing for it to annotate. */}
			{manpower.length > 0 && (date.audit_trail || []).length > 0 && (
				<AuditTrail>
					<div className="audit-title">Audit trail</div>
					<div className="audit-summary">
						Entry date <strong>{date.entry_date}</strong>
						{date.uploaded_on && (
							<>
								{' '}
								· recorded on <strong>{date.uploaded_on}</strong>
							</>
						)}
						{date.days_late > 0 && ` · ${date.days_late} day(s) after the entry date`}
					</div>
					{date.audit_trail.map((row) => (
						<div className="audit-line" key={`${row.at}-${row.action}`}>
							{formatIstDateTime(row.at)}
							{row.by ? ` · ${row.by}` : ''} · {row.action}
							{row.upload_batch_id ? ` (batch #${row.upload_batch_id})` : ''}
						</div>
					))}
				</AuditTrail>
			)}

			{breakdownOpen && (
				<SlabBreakdownModal
					open
					title={`Shipment Revenue as of ${moment(date.entry_date).format('DD-MM-YYYY')}`}
					// Month-to-date through this date, not just this day's own slice — the
					// modal title already says "as of", so it should show everything that
					// led up to it, the way earlier slabs get consumed by the days before.
					// Falls back to this day's own slice if the parent didn't supply one.
					breakdown={cumulativeBreakdown ?? slabBreakdown}
					shipmentCount={cumulativeShipmentCount ?? shipmentTotal}
					onClose={() => setBreakdownOpen(false)}
				/>
			)}
		</Box>
	);
}

DailyDatePanel.propTypes = {
	date: PropTypes.object.isRequired,
	// Month-to-date slab breakdown/count through this date (this date plus every
	// earlier date in the same period), computed by the parent since it alone holds
	// every date's row. Falls back to this date's own slice when not supplied.
	cumulativeBreakdown: PropTypes.array,
	cumulativeShipmentCount: PropTypes.number,
	canDelete: PropTypes.bool,
	costFrozen: PropTypes.bool,
	canEditCounts: PropTypes.bool,
	revenueFrozen: PropTypes.bool,
	revenueFrozenReason: PropTypes.string,
	bagRate: PropTypes.number,
	bagRateLabel: PropTypes.string,
	isCostPlus: PropTypes.bool,
	onDelete: PropTypes.func,
	onUpdateCount: PropTypes.func,
};

DailyDatePanel.defaultProps = {
	cumulativeBreakdown: undefined,
	cumulativeShipmentCount: undefined,
	canDelete: false,
	costFrozen: false,
	canEditCounts: false,
	revenueFrozen: false,
	revenueFrozenReason: undefined,
	bagRate: undefined,
	bagRateLabel: undefined,
	isCostPlus: false,
	onDelete: () => {},
	onUpdateCount: () => {},
};
